import React, { useState, useEffect } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Form, Accordion, Card } from 'react-bootstrap';
import AddMaintenance from './PopUps/Add/AddMaintenance';
import EditMaintenance from './PopUps/Edit/EditMaintenance';
import * as Icon from 'react-feather';
import { canPageAction } from '../../config/roleConfig';
import DeactivateConfirmModal from '../Common/DeactivateConfirmModal';
import { MEXICO_CITY_TIME_ZONE } from '../../utils/dateTime';

function Maintenance(props)  {
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const currentUser = authUser();
    const canRead = canPageAction(currentUser, 'operations.maintenance', 'read');
    const canWrite = canPageAction(currentUser, 'operations.maintenance', 'write');
    const canDelete = canPageAction(currentUser, 'operations.maintenance', 'delete');

    const [data, setData] = useState([]);
    const [inventoryOptions, setInventoryOptions] = useState([]);
    const [selectedInventoryId, setSelectedInventoryId] = useState('');
    const [maintenanceAreas, setMaintenanceAreas] = useState([]);
    const [expandedMaintenance, setExpandedMaintenance] = useState(null);
    const [maintenanceToDeactivate, setMaintenanceToDeactivate] = useState(null);
    const [deactivatingMaintenance, setDeactivatingMaintenance] = useState(false);
    const apiUrl = process.env.REACT_APP_URL;

    const get_all_data = (inventoryId = '') => {
        let url = apiUrl + '/maintenance/maintenance';
        if (inventoryId) {
            url += `?inventory_id=${inventoryId}`;
        }

        axios({
            method:'GET',
            url: url,
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Maintenance || []);
        })
        .catch(error => {
            console.error('Error fetching maintenance data:', error);
            alert('Error al cargar datos de mantenimiento');
        });
    }

    const get_inventory_options = () => {
        axios({
            method: 'GET',
            url: apiUrl + '/units/get_all_units',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setInventoryOptions(response.data.msg.Units || []);
        })
        .catch(error => {
            console.error('Error fetching inventory options:', error);
        });
    }

    const get_maintenance_areas = () => {
        axios({
            method: 'GET',
            url: apiUrl + '/maintenance/areas',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setMaintenanceAreas(response.data.msg.MaintenanceAreas || []);
        })
        .catch(error => {
            console.error('Error fetching maintenance areas:', error);
        });
    }

    useEffect(() => {
        get_inventory_options();
        get_maintenance_areas();
    }, []);

    useEffect(() => {
        if (inventoryOptions.length > 0) {
            const firstUnitId = inventoryOptions[0].id;
            setSelectedInventoryId(firstUnitId);
            get_all_data(firstUnitId);
        }
    }, [inventoryOptions]);

    const handleInventoryChange = (e) => {
        const inventoryId = e.target.value;
        setSelectedInventoryId(inventoryId);
        get_all_data(inventoryId);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'passed':
                return <Badge bg="success">Aprobado</Badge>;
            case 'failed':
                return <Badge bg="danger">Fallido</Badge>;
            case 'needs_attention':
                return <Badge bg="warning">Requiere Atención</Badge>;
            case 'not_applicable':
                return <Badge bg="secondary">No Aplica</Badge>;
            case 'pending':
                return <Badge bg="info">Pendiente</Badge>;
            default:
                return <Badge bg="light">{status}</Badge>;
        }
    };

    const getMaintenanceTypeBadge = (type) => {
        return type === 'preventive' ?
            <Badge bg="primary">Preventivo</Badge> :
            <Badge bg="warning">Correctivo</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-MX', {
            timeZone: MEXICO_CITY_TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const toggleMaintenanceDetails = (maintenanceId) => {
        setExpandedMaintenance(expandedMaintenance === maintenanceId ? null : maintenanceId);
    };

    const deleteMaintenance = (maintenance) => {
        if (!maintenance) {
            return;
        }

        setDeactivatingMaintenance(true);
        axios({
            method: 'DELETE',
            url: apiUrl + `/maintenance/${maintenance.id}`,
            headers: {
                Authorization: authHeader()
            }
        }).then(() => {
            setMaintenanceToDeactivate(null);
            refreshData();
        }).catch(error => {
            alert(error.response?.data?.error || 'No se pudo desactivar el mantenimiento');
            console.log(error);
        }).finally(() => {
            setDeactivatingMaintenance(false);
        });
    };

    const refreshData = () => {
        get_all_data(selectedInventoryId);
    };

    return (
        <>
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between">
                    <h5 className="card-title">Registros de Mantenimiento</h5>
                    {canWrite && (
                        <AddMaintenance
                            setData={refreshData}
                            inventoryOptions={inventoryOptions}
                            maintenanceAreas={maintenanceAreas}
                        />
                    )}
                </div>

                <div className="mb-3 d-flex align-items-center">
                    <Form.Group className="d-flex align-items-center">
                        <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Filtrar por Unidad:</Form.Label>
                        <Form.Select
                            value={selectedInventoryId}
                            onChange={handleInventoryChange}
                            style={{width: '250px'}}
                        >
                            {inventoryOptions.map(inventory => (
                                <option key={inventory.id} value={inventory.id}>
                                    {inventory.inventory_name || inventory.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>

                <div>
                    <Table className="m-0" responsive>
                        <thead>
                            <tr>
                                <th>Unidad</th>
                                <th>Tipo</th>
                                <th>Ubicación</th>
                                <th>Fecha Ingreso</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Fin</th>
                                <th>Costo Total</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(maintenance => (
                                <React.Fragment key={maintenance.id}>
                                    <tr>
                                        <td>{maintenance.inventory_name}</td>
                                        <td>{getMaintenanceTypeBadge(maintenance.maintenance_type)}</td>
                                        <td>{maintenance.maintenance_location}</td>
                                        <td>{formatDate(maintenance.date_entered)}</td>
                                        <td>{formatDate(maintenance.date_started)}</td>
                                        <td>{formatDate(maintenance.date_finished)}</td>
                                        <td>${parseFloat(maintenance.total_cost || 0).toFixed(2)}</td>
                                        <td>
                                            {maintenance.is_completed ?
                                                <Badge bg="success">Completado</Badge> :
                                                <Badge bg="warning">En Proceso</Badge>
                                            }
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {canRead && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => toggleMaintenanceDetails(maintenance.id)}
                                                    >
                                                        <Icon.Eye size={14} />
                                                    </Button>
                                                )}
                                                {canWrite && (
                                                    <EditMaintenance
                                                        setData={refreshData}
                                                        maintenanceInfo={maintenance}
                                                        inventoryOptions={inventoryOptions}
                                                        maintenanceAreas={maintenanceAreas}
                                                    />
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => setMaintenanceToDeactivate(maintenance)}
                                                    >
                                                        <Icon.Trash2 size={14} />
                                                    </Button>
                                                )}
                                                {!canRead && !canWrite && !canDelete && (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedMaintenance === maintenance.id && (
                                        <tr>
                                            <td colSpan="9">
                                                <div className="p-3 bg-light">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <h6>Información del Mantenimiento</h6>
                                                            <p><strong>Técnico:</strong> {maintenance.technician_name || 'N/A'}</p>
                                                            <p><strong>Diagnósticos:</strong> {maintenance.diagnostics || 'N/A'}</p>
                                                            <p><strong>Resultados:</strong> {maintenance.results || 'N/A'}</p>
                                                            <p><strong>Notas Adicionales:</strong> {maintenance.additional_notes || 'N/A'}</p>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <h6>Verificaciones Realizadas</h6>
                                                            {maintenance.maintenance_checks && maintenance.maintenance_checks.length > 0 ? (
                                                                <div>
                                                                    {maintenance.maintenance_checks.map(check => (
                                                                        <div key={check.id} className="mb-2 p-2 border rounded">
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <strong>{check.maintenance_item_name}</strong>
                                                                                {getStatusBadge(check.status)}
                                                                            </div>
                                                                            <small className="text-muted">
                                                                                Área: {check.maintenance_area_name}
                                                                            </small>
                                                                            {check.cost > 0 && (
                                                                                <div><small>Costo: ${parseFloat(check.cost).toFixed(2)}</small></div>
                                                                            )}
                                                                            {check.observations && (
                                                                                <div><small>Observaciones: {check.observations}</small></div>
                                                                            )}
                                                                            {check.parts_replaced && (
                                                                                <div><small>Partes reemplazadas: {check.parts_replaced}</small></div>
                                                                            )}
                                                                            {check.measurements && (
                                                                                <div><small>Mediciones: {check.measurements}</small></div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-muted">No hay verificaciones registradas</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {data.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="text-center text-muted">
                                        No hay registros de mantenimiento para esta unidad
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
        <DeactivateConfirmModal
            show={Boolean(maintenanceToDeactivate)}
            title="Desactivar Mantenimiento"
            itemLabel={maintenanceToDeactivate?.inventory_name}
            confirming={deactivatingMaintenance}
            onCancel={() => !deactivatingMaintenance && setMaintenanceToDeactivate(null)}
            onConfirm={() => deleteMaintenance(maintenanceToDeactivate)}
        />
        </>
    );
}

export default Maintenance;
